'use client'

import { isMac } from '@shared/utils/platform'
import { useEffect, useRef, type RefObject } from 'react'

// Get caret position within plain text content of a contenteditable element.
export const getPlainTextCaretPosition = (el: HTMLElement): number => {
  const sel = window.getSelection()
  if (!sel || !sel.rangeCount) {
    return 0
  }
  const range = sel.getRangeAt(0)
  const preCaretRange = range.cloneRange()
  preCaretRange.selectNodeContents(el)
  preCaretRange.setEnd(range.endContainer, range.endOffset)
  return preCaretRange.toString().length
}

// Restore caret to a specific plain-text offset within a contenteditable element.
export const restorePlainTextCaretPosition = (el: HTMLElement, position: number): void => {
  const range = document.createRange()
  const sel = window.getSelection()
  let charCount = 0

  const traverseNodes = (node: Node): boolean => {
    if (node.nodeType === Node.TEXT_NODE) {
      const len = node.textContent?.length ?? 0
      if (charCount + len >= position) {
        range.setStart(node, position - charCount)
        range.setEnd(node, position - charCount)
        return true
      }
      charCount += len
    } else {
      for (let i = 0; i < node.childNodes.length; i++) {
        if (traverseNodes(node.childNodes[i])) {
          return true
        }
      }
    }
    return false
  }

  traverseNodes(el)
  if (sel) {
    sel.removeAllRanges()
    sel.addRange(range)
  }
}

const getAbsoluteOffset = (root: HTMLElement, node: Node, offset: number): number => {
  const r = document.createRange()
  r.selectNodeContents(root)
  r.setEnd(node, offset)
  return r.toString().length
}

const readTargetRange = (root: HTMLElement, ie: InputEvent): { text: string; start: number; length: number } => {
  const ranges =
    typeof (ie as InputEvent & { getTargetRanges?: () => StaticRange[] }).getTargetRanges === 'function'
      ? (ie as InputEvent & { getTargetRanges: () => StaticRange[] }).getTargetRanges()
      : []
  if (ranges.length === 0) {
    return { text: '', start: 0, length: 0 }
  }
  const sr = ranges[0]
  const r = document.createRange()
  r.setStart(sr.startContainer, sr.startOffset)
  r.setEnd(sr.endContainer, sr.endOffset)
  return {
    text: r.toString(),
    start: getAbsoluteOffset(root, sr.startContainer, sr.startOffset),
    length: r.toString().length,
  }
}

/**
 * Patches Chromium's broken interaction with macOS text replacement /
 * autocorrect on a contenteditable element. Targets the same UX as native
 * macOS text replacement in Slack / Notion:
 *
 * - Clicking the suggestion bubble accepts the replacement.
 * - Pressing an "accept" key (space, return, tab, punctuation) accepts
 *   and the accept character is appended (e.g. "omw " -> "On my way! ").
 * - Pressing any other character (letters, digits) dismisses the
 *   suggestion and keeps what the user actually typed.
 * - Pressing Backspace while the bubble is live deletes a single
 *   character, not the entire candidate word, and does NOT trigger a
 *   "revert autocorrect" selection.
 *
 * Three separate behaviors have to be handled, all reachable through
 * `beforeinput` with inputType=`insertText`:
 *
 *   (1) Genuine autocorrect replacement. macOS dispatches a SECOND
 *       `beforeinput` right after the accept-key's own insertion,
 *       with multi-char `data` (the replacement) and a target range
 *       covering the trigger text (e.g. "omw"). Legitimate IME input
 *       uses a different inputType, so IME is unaffected.
 *
 *   (2) Phantom replacement replay. After the user space-accepts a
 *       correction — or backspaces immediately after accepting —
 *       Chromium fires ANOTHER multi-char `beforeinput` within that
 *       same keystroke whose `data` already sits in the DOM at the
 *       target range's start (e.g. trying to re-insert "On my way!"
 *       at offset 0 when "On my way!" is already there). Without
 *       intervention this either duplicates the replacement on a
 *       second space, or leaves the selection spanning the first
 *       few chars of the committed replacement (which the next
 *       backspace then erases — the "first Backspace highlights 'On '"
 *       bug). We detect these via the "data already present at
 *       target start" check and cancel them unconditionally.
 *
 *   (3) Wide-range bare-Backspace. While an autocorrect candidate
 *       is active, Chromium can turn a bare Backspace into
 *       deleteWordBackward with the whole candidate word as the
 *       target. Force single-char deletion in that case.
 *
 * Designed for contenteditable elements only — native `<input>` and
 * `<textarea>` elements handle macOS autocorrect correctly without
 * intervention and use a different selection model.
 *
 * Returns a handle exposing `shouldDeferUndoToNative()`: true (one-shot)
 * when the most recent committed input was an accepted autocorrect
 * replacement. macOS registers a native "Change Back to …" undo action for
 * the correction; a custom JS undo that rewrites the DOM while that action
 * is pending collides with it and wedges the window's text input context
 * (keystrokes get swallowed until another cmd+Z). Callers with their own
 * undo stack should let that one cmd+Z fall through to the native undo
 * instead.
 */
export interface MacOsAutocorrectFixHandle {
  shouldDeferUndoToNative: () => boolean
}

export function useMacOsAutocorrectFix(ref: RefObject<HTMLElement | null>): MacOsAutocorrectFixHandle {
  // Set on the beforeinput of a genuine replacement, promoted to
  // `lastCommitWasAutocorrect` by the matching input event, cleared again by
  // any later input. Refs (not state) so the handle stays stable and reads
  // are synchronous inside key handlers.
  const pendingAutocorrectCommit = useRef(false)
  const lastCommitWasAutocorrect = useRef(false)
  const handleRef = useRef<MacOsAutocorrectFixHandle>({
    shouldDeferUndoToNative: () => {
      const defer = lastCommitWasAutocorrect.current
      // One-shot: if the native undo turns out to be a no-op, the next
      // cmd+Z falls back to the caller's own undo stack.
      lastCommitWasAutocorrect.current = false
      return defer
    },
  })

  useEffect(() => {
    if (!isMac) {
      return
    }

    const el = ref.current
    if (!el) {
      return
    }

    // Characters that accept the current autocorrect suggestion. The
    // actual list macOS uses is larger than just whitespace; matching
    // Slack's "space and basic punctuation" feels natural.
    const acceptKeyPattern = /^[\s.,!?;:'")\]}\-/\\]$/

    let keystrokeInFlight = false
    let lastKeyIsAccept = false
    let lastKeyIsBareBackspace = false
    let keyDownHadSelection = false
    let caretAtKeyDown: number | null = null
    // Caret position after the most recent user-facing input event.
    // Used to restore the caret when we cancel a phantom replacement
    // whose preceding input moved the selection.
    let lastInputCaret: number | null = null

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target !== el) {
        return
      }
      keystrokeInFlight = true
      // Clear on the next macrotask so the flag survives the full
      // keydown -> beforeinput -> input -> beforeinput(replacement)
      // event dispatch (microtasks drain between each of those, so
      // queueMicrotask would clear too early). A later click on the
      // suggestion bubble arrives on a separate task, by which time
      // the flag has been reset.
      setTimeout(() => {
        keystrokeInFlight = false
      }, 0)
      const isBareKey = !e.metaKey && !e.ctrlKey && !e.altKey
      const isReturnOrTab = e.key === 'Enter' || e.key === 'Tab'
      lastKeyIsAccept = isBareKey && (isReturnOrTab || (e.key.length === 1 && acceptKeyPattern.test(e.key)))
      lastKeyIsBareBackspace = e.key === 'Backspace' && isBareKey && !e.shiftKey
      const sel = window.getSelection()
      const hasSel = !!(sel && sel.rangeCount > 0 && !sel.getRangeAt(0).collapsed)
      keyDownHadSelection = hasSel
      caretAtKeyDown = hasSel ? null : getPlainTextCaretPosition(el)
    }

    const handleInput = (e: Event) => {
      const ie = e as InputEvent
      if (ie.target !== el) {
        return
      }
      // Track the caret after every genuine input. Phantom dismissal
      // restores to this so the selection doesn't get stuck spanning
      // the range Chromium extended it over.
      lastInputCaret = getPlainTextCaretPosition(el)
      // The input event for the replacement itself promotes the pending
      // flag; any other input clears it.
      lastCommitWasAutocorrect.current = pendingAutocorrectCommit.current
      pendingAutocorrectCommit.current = false
    }

    const handleBeforeInput = (e: Event) => {
      const ie = e as InputEvent
      if (ie.target !== el) {
        return
      }

      // Multi-char insertText: genuine replacement OR phantom replay.
      if (ie.inputType === 'insertText' && ie.data && ie.data.length > 1 && ie.cancelable) {
        const fullText = el.textContent ?? ''
        // The keystroke that triggered this replacement can have upstream
        // side effects that empty the editor before macOS dispatches it —
        // Enter in the chat input sends the message and clears the field.
        // With the trigger text gone there is nothing to replace; accepting
        // would paste the replacement into the cleared editor. Dismiss.
        if (keystrokeInFlight && fullText.replace(/\u200B/g, '').length === 0) {
          ie.preventDefault()
          return
        }
        const range = readTargetRange(el, ie)
        // Phantom: Chromium is trying to "insert" text that's already
        // sitting in the DOM at the target position. This happens on
        // the second space after an accept, and on the Backspace
        // immediately after an accept. Kill it unconditionally.
        const alreadyPresent =
          ie.data.length > 0 && fullText.substring(range.start, range.start + ie.data.length) === ie.data
        if (alreadyPresent) {
          ie.preventDefault()
          // Chromium has moved the selection to span `range` for the
          // would-be replacement; restore the caret to wherever the
          // preceding real input left it.
          if (lastInputCaret !== null) {
            restorePlainTextCaretPosition(el, lastInputCaret)
          }
          return
        }
        // Click on the suggestion bubble: no preceding keydown, so
        // keystrokeInFlight is false. Accept natively.
        if (!keystrokeInFlight) {
          pendingAutocorrectCommit.current = true
          return
        }
        // Accept key (space / return / tab / punctuation): let
        // Chromium commit the replacement natively.
        if (lastKeyIsAccept) {
          pendingAutocorrectCommit.current = true
          return
        }
        // Otherwise the user is typing past the candidate — dismiss
        // the replacement and restore the caret to where their own
        // keystroke actually left it.
        ie.preventDefault()
        if (lastInputCaret !== null) {
          restorePlainTextCaretPosition(el, lastInputCaret)
        }
        return
      }

      // Bare Backspace while an autocorrect candidate is active can
      // fire deleteWordBackward / deleteContentBackward with the whole
      // candidate word as the target range. Force single-char delete.
      if (lastKeyIsBareBackspace && !keyDownHadSelection && ie.cancelable) {
        if (ie.inputType === 'deleteContentBackward' || ie.inputType === 'deleteWordBackward') {
          const range = readTargetRange(el, ie)
          if (range.length > 1) {
            ie.preventDefault()
            if (caretAtKeyDown !== null) {
              restorePlainTextCaretPosition(el, caretAtKeyDown)
            }
            document.execCommand('delete')
          }
        }
      }
    }

    el.addEventListener('keydown', handleKeyDown, true)
    el.addEventListener('beforeinput', handleBeforeInput, true)
    el.addEventListener('input', handleInput, true)
    return () => {
      el.removeEventListener('keydown', handleKeyDown, true)
      el.removeEventListener('beforeinput', handleBeforeInput, true)
      el.removeEventListener('input', handleInput, true)
    }
  }, [ref])

  return handleRef.current
}
